import { Button } from '@/global/components/Button'
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from '@/global/components/ui/dialog'
import { cn } from '@/lib/utils'
import {
    ChevronDownIcon,
    DeleteIcon,
    DivideIcon,
    EqualIcon,
    MinusIcon,
    PlusIcon,
    XIcon,
} from 'lucide-react'
import { type PointerEvent, useRef, useState } from 'react'
import { useStore } from 'store'
import IconButton from '../Button/IconButton'
import { Input } from '../ui/input'
import { avaliar } from './expressao'
import { formatarMinutos, formatarTotalMinutos } from './formatar'

const LARGURA = 320
const CARACTERES_VALIDOS = /^[0-9:+\-*/() ]*$/

const TECLAS: {
    rotulo: React.ReactNode
    insere: string
    destaque?: boolean
}[] = [
    {
        rotulo: <span className="font-semibold">C</span>,
        insere: 'limpar',
        destaque: true,
    },
    { rotulo: <span className="font-semibold">{'('}</span>, insere: '(' },
    { rotulo: <span className="font-semibold">{')'}</span>, insere: ')' },
    { rotulo: <DeleteIcon />, insere: 'apagar', destaque: true },
    { rotulo: '7', insere: '7' },
    { rotulo: '8', insere: '8' },
    { rotulo: '9', insere: '9' },
    { rotulo: <DivideIcon />, insere: '/' },
    { rotulo: '4', insere: '4' },
    { rotulo: '5', insere: '5' },
    { rotulo: '6', insere: '6' },
    { rotulo: <XIcon />, insere: '*' },
    { rotulo: '1', insere: '1' },
    { rotulo: '2', insere: '2' },
    { rotulo: '3', insere: '3' },
    { rotulo: <MinusIcon />, insere: '-' },
    { rotulo: '0', insere: '0' },
    { rotulo: <span className="font-semibold">:</span>, insere: ':' },
    { rotulo: <EqualIcon />, insere: 'calcular', destaque: true },
    { rotulo: <PlusIcon />, insere: '+' },
]

interface Posicao {
    x: number
    y: number
}

interface LinhaHistorico {
    expressao: string
    minutos: number
}

const posicaoInicial = (): Posicao => ({
    x: Math.max(16, window.innerWidth / 2 - LARGURA / 2),
    y: 96,
})

const limitarNaTela = ({ x, y }: Posicao): Posicao => ({
    x: Math.min(Math.max(0, x), Math.max(0, window.innerWidth - LARGURA)),
    y: Math.min(Math.max(0, y), Math.max(0, window.innerHeight - 80)),
})

/** Renderiza a janela flutuante para calcular expressões de horas. */
export function CalculadoraHoras() {
    const isAberta = useStore(state => state.isCalculadoraAberta)
    const fechar = useStore(state => state.fecharCalculadora)

    const [posicao, setPosicao] = useState<Posicao>(posicaoInicial)
    const [isMinimizado, setMinimizado] = useState(false)
    const [expressao, setExpressao] = useState('')
    const [minutos, setMinutos] = useState<number | null>(null)
    const [erro, setErro] = useState<string | null>(null)
    const [historico, setHistorico] = useState<LinhaHistorico[]>([])

    const inputRef = useRef<HTMLInputElement>(null)
    const arrasteRef = useRef<Posicao | null>(null)

    const aoAbrirOuFechar = (proximo: boolean) => {
        if (proximo) return

        fechar()
        setExpressao('')
        setMinutos(null)
        setErro(null)
        setHistorico([])
        setPosicao(posicaoInicial())
    }

    const aoPressionarCabecalho = (evento: PointerEvent<HTMLDivElement>) => {
        evento.currentTarget.setPointerCapture(evento.pointerId)
        arrasteRef.current = {
            x: evento.clientX - posicao.x,
            y: evento.clientY - posicao.y,
        }
    }

    const aoMoverCabecalho = (evento: PointerEvent<HTMLDivElement>) => {
        const inicio = arrasteRef.current
        if (!inicio) return

        setPosicao(
            limitarNaTela({
                x: evento.clientX - inicio.x,
                y: evento.clientY - inicio.y,
            }),
        )
    }

    const aoSoltarCabecalho = (evento: PointerEvent<HTMLDivElement>) => {
        arrasteRef.current = null
        evento.currentTarget.releasePointerCapture(evento.pointerId)
    }

    const calcular = () => {
        const resultado = avaliar(expressao)

        if (!resultado.ok) {
            setErro(resultado.erro)
            setMinutos(null)
            return
        }

        setErro(null)
        setMinutos(resultado.minutos)
        setHistorico(anterior =>
            [{ expressao, minutos: resultado.minutos }, ...anterior].slice(
                0,
                50,
            ),
        )
    }

    const anexar = (texto: string) => {
        setErro(null)
        setExpressao(anterior => anterior + texto)
        inputRef.current?.focus()
    }

    const aoClicarTecla = (insere: string) => {
        if (insere === 'calcular') return calcular()

        if (insere === 'limpar') {
            setExpressao('')
            setMinutos(null)
            setErro(null)
            inputRef.current?.focus()
            return
        }

        if (insere === 'apagar') {
            setErro(null)
            setExpressao(anterior => anterior.slice(0, -1))
            inputRef.current?.focus()
            return
        }

        anexar(insere)
    }

    const reaproveitar = (linha: LinhaHistorico) => {
        setErro(null)
        setExpressao(formatarMinutos(linha.minutos))
        setMinutos(null)
        inputRef.current?.focus()
    }

    return (
        <Dialog open={isAberta} onOpenChange={aoAbrirOuFechar} modal={false}>
            <DialogContent
                showOverlay={false}
                className={cn(
                    'fixed top-0 left-0 translate-x-0 translate-y-0',
                    'w-[320px] max-w-[calc(100vw-2rem)] gap-0 p-0',
                    'sm:max-w-[320px] transition-[scale] duration-200',
                    { 'scale-0': !isAberta },
                )}
                style={{
                    transform: `translate3d(${posicao.x}px, ${posicao.y}px, 0)`,
                }}
                onPointerDownOutside={evento => evento.preventDefault()}
                onInteractOutside={evento => evento.preventDefault()}
                onOpenAutoFocus={evento => {
                    evento.preventDefault()
                    inputRef.current?.focus()
                }}
                showCloseButton={false}
            >
                <DialogHeader
                    onPointerDown={aoPressionarCabecalho}
                    onPointerMove={aoMoverCabecalho}
                    onPointerUp={aoSoltarCabecalho}
                    className={cn(
                        'cursor-grab touch-none select-none active:cursor-move',
                        'border-b px-4 py-2 h-12 justify-center transition-colors duration-200',
                        {
                            'border-b-transparent': isMinimizado,
                        },
                    )}
                >
                    <DialogTitle>Calculadora de horas</DialogTitle>
                    <DialogDescription className="sr-only">
                        Some e subtraia horas no formato HH:mm ou em minutos.
                    </DialogDescription>
                </DialogHeader>

                <div
                    className={cn(
                        'max-h-screen overflow-auto [scrollbar-width:none] transition-[max-height] duration-200',
                        {
                            'max-h-0': isMinimizado,
                        },
                    )}
                >
                    <div className="inline-flex right-1 top-1 absolute z-50 *:text-foreground">
                        <IconButton
                            onClick={() => setMinimizado(prev => !prev)}
                            title="Minimizar/Maximizar"
                        >
                            <ChevronDownIcon
                                className={cn('transition-all duration-200', {
                                    'rotate-180': isMinimizado,
                                })}
                            />
                        </IconButton>
                        <IconButton
                            onClick={() => aoAbrirOuFechar(false)}
                            title="Fechar"
                        >
                            <XIcon />
                        </IconButton>
                    </div>
                    <div className="flex h-32 flex-col-reverse gap-1 overflow-y-auto px-4 py-2 text-right text-xs text-muted-foreground">
                        {historico.map((linha, indice) => (
                            <Button
                                key={`${linha.expressao}-${indice}`}
                                onClick={() => reaproveitar(linha)}
                                className="cursor-pointer truncate text-right hover:text-foreground justify-end"
                                variant="text"
                            >
                                {linha.expressao} ={' '}
                                {formatarMinutos(linha.minutos)}
                            </Button>
                        ))}
                    </div>

                    <div className="border-t px-4 py-2">
                        <Input
                            ref={inputRef}
                            value={expressao}
                            inputMode="text"
                            aria-label="Expressão"
                            onChange={evento => {
                                if (
                                    !CARACTERES_VALIDOS.test(
                                        evento.target.value,
                                    )
                                )
                                    return
                                setErro(null)
                                setExpressao(evento.target.value)
                            }}
                            onKeyDown={evento => {
                                if (evento.key !== 'Enter') return
                                evento.preventDefault()
                                calcular()
                            }}
                            variant="outlined"
                            className="[&_input]:text-right text-xl"
                        />

                        <div className="h-10 text-right">
                            {erro && (
                                <span className="text-sm text-destructive">
                                    {erro}
                                </span>
                            )}
                            {!erro && minutos !== null && (
                                <>
                                    <div className="text-2xl font-semibold">
                                        {formatarMinutos(minutos)}
                                    </div>
                                    <div className="text-xs text-muted-foreground">
                                        {formatarTotalMinutos(minutos)}
                                    </div>
                                </>
                            )}
                        </div>
                    </div>

                    <div className="grid grid-cols-4 gap-1 border-t p-2">
                        {TECLAS.map(tecla => (
                            <Button
                                key={tecla.insere}
                                variant={
                                    tecla.destaque ? 'contained' : 'outlined'
                                }
                                onClick={() => aoClicarTecla(tecla.insere)}
                                className="h-10 text-base"
                                title={tecla.insere}
                            >
                                {tecla.rotulo}
                            </Button>
                        ))}
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    )
}
